import type { BaseOptionType } from "antd/lib/select"

import type { StoredPage } from "../../../redux/shared/paged-list.ts"
import type { Attachment } from "../../Album/types/attachment.types.ts"
import { type SaveResult } from "../../../redux/shared/types.ts"
import type { BaseFilters, BasePagination } from "../../../types/api.ts"
import type { AuditLogEntry } from "../../../types/audit-log.types.ts"
import { type FinancialAlertsPayload } from "./project-finance/project-finance.thunks.ts"
import { type ProjectChecklistItem } from "../types/project-checklists.types.tsx"
import type {
  FinancialTransactionItem,
  FinancialTransactionSnapshot,
  FinancialTransactionSummary
} from "../types/project-finance.types.tsx"
import {
  type InvoiceSnapshot,
  type ProjectInvoice
} from "../types/project-invoices.types.tsx"
import type { MilestoneDependency } from "../types/project-milestone-dependency.types.ts"
import type { ProjectMilestoneSnapshot } from "./project-milestones/project-milestones.types.ts"
import type { ProjectMilestone } from "../types/project-phases.types.tsx"
import type { ProjectSubcontractor } from "../types/project-subcontractors.types.tsx"
import { type Project } from "../types/project.types.ts"
import { type ProjectCommissionsData } from "./project-commissions/project-commissions.types.ts"

interface ProjectFinancialTransactionFilters extends BaseFilters {
  item_type: null | number[]
  project_type: null | string[]
  project_phase: null | string[]
  status: null | number[]
  entry_type: null | string[]
}

interface ProjectInvoiceFilters extends BaseFilters {
  type: null | number
  status: null | number
}

interface ProjectChecklistFilters extends BaseFilters {
  assigned_to?: string
  project_phase?: string | null
  project_type?: string
  overdue?: boolean
  status?: string
  priority?: string
}

type ProjectCommissionsFilters = BaseFilters

interface ProjectMilestoneFilters extends BaseFilters {
  project_type: string | null
  /** The open tab. `project_type` stays: the finance filters still key on it. */
  project_type_assignment?: string | null
}

interface ProjectSubcontractorFilters extends BaseFilters {
  ordering?: string | null
}

type ProjectMilestoneFilterOptionsKeys =
  | "checklist_item_status"
  | "status"
  | "project_managers"
  | "admins"
  | "subcontractors"
  | "category"

interface ProjectMilestoneFilterOptions extends Partial<
  Record<ProjectMilestoneFilterOptionsKeys, BaseOptionType[]>
> {
  subcontractors?: Array<{
    label: string
    value: { id: string | null; contact_user: string | null }
  }>
}

type ProjectChecklistFilterOptionsKeys =
  | "project_phases"
  | "status"
  | "subcontractors"
  | "team_members"
  | "project_managers"
  | "admins"
  | "priority"
  | "milestones"

interface ProjectChecklistFilterOptions extends Record<
  ProjectChecklistFilterOptionsKeys,
  BaseOptionType[]
> {
  project_type: Array<BaseOptionType & { milestones: BaseOptionType[] }>
}

const FETCHING_SNAPSHOT_ENUM = {
  FINANCIAL: "financial",
  INVOICE: "invoice",
  MILESTONES: "milestones"
} as const

type FetchingSnapshotEnum =
  (typeof FETCHING_SNAPSHOT_ENUM)[keyof typeof FETCHING_SNAPSHOT_ENUM]

type RefreshableProjectList =
  | "checklistItems"
  | "financialTransactionItems"
  | "invoices"
  | "subcontractors"

/** A read the project page starts as soon as it knows the project id. */
type ProjectPageRead =
  | "milestoneSnapshot"
  | "invoiceSnapshot"
  | "milestoneDependencies"
  | "milestonesFilterOptions"
  | "financeFilterOptions"

interface ProjectPageReadState {
  projectId: string
  requestId: string
  status: "pending" | "done" | "failed"
  /** Started by the page open and not yet taken by the screen that shows it. */
  offered: boolean
  /** When the page open started it (ms since epoch). */
  offeredAt?: number
}

/** The open project's own files, which its details read leaves out. */
interface ProjectFilesState {
  projectId: string | null
  requestId: string | null
  status: "idle" | "loading" | "done" | "failed"
  items: Attachment[]
}

interface ProjectState {
  project: Project | null
  projectFiles: ProjectFilesState
  /** Only the newest project read may land. */
  projectRequestId: string | null
  pageReads: Partial<Record<ProjectPageRead, ProjectPageReadState>>
  milestoneSnapshot: ProjectMilestoneSnapshot | null
  financeFilterOptions: Record<string, BaseOptionType[]>

  // milestones
  milestones: StoredPage<ProjectMilestone>
  milestonesRequestId: string | null
  /** Counts milestone writes; a read records the count it started at. */
  milestoneWriteSeq: number
  /** The write count at each row's latest write: a read that started before
   * it carries that row's pre-write values and must not replace it. */
  milestoneRowWrittenAt: Record<string, number>
  /** The write count each in-flight milestone read started at, by request. */
  milestoneReadStartedAt: Record<string, number>
  milestonesFilters: ProjectMilestoneFilters
  milestonesFilterOptions: ProjectMilestoneFilterOptions
  // Flat, project-scoped — fetched once alongside the milestone list and
  // consumed by both the table's dependency count and the Gantt view's
  // connector arrows; not paginated/filtered like the milestones themselves.
  milestoneDependencies: MilestoneDependency[]

  // financial transaction items
  financialTransactionItems: StoredPage<
    FinancialTransactionItem,
    FinancialTransactionSummary
  >
  financialTransactionItemsRequestId: string | null
  financialTransactionSnapshot: FinancialTransactionSnapshot | null
  financialAlerts: FinancialAlertsPayload | null
  financialAlertsMuteRequestId: string | null
  financialSnapshotRequestedFor: string | null
  /** Only the newest snapshot / alerts request may write: a save refetches
   *  while a mount-time read can still be out. */
  financialSnapshotRequestId: string | null
  financialAlertsRequestId: string | null
  financialAlertsRequestedFor: string | null
  alertContributingMilestones: ProjectMilestone[]
  financialTransactionFilters: ProjectFinancialTransactionFilters

  // invoices
  invoices: StoredPage<ProjectInvoice>
  invoicesRequestId: string | null
  invoiceSnapshot: InvoiceSnapshot | null
  invoicesFilters: ProjectInvoiceFilters

  // subcontractors
  subcontractors: StoredPage<ProjectSubcontractor>
  subcontractorsRequestId: string | null
  selectedSubcontractor: ProjectSubcontractor | null
  subcontractorsFilters: ProjectSubcontractorFilters

  // checklist
  checklistItems: StoredPage<ProjectChecklistItem>
  checklistItemsRequestId: string | null
  checklistItemsFilters: ProjectChecklistFilters
  checklistItemsFilterOptions: ProjectChecklistFilterOptions

  /** The pending re-read of each list, which a load-more may not displace. */
  listRefreshRequestIds: Partial<Record<RefreshableProjectList, string>>

  // commissions
  commissions: ProjectCommissionsData
  commissionsItemsFilters: ProjectCommissionsFilters

  // logs
  logs: AuditLogEntry[]
  logsRequestId: string | null

  showSnapshot: boolean
  // Set by the header's alert icon, consumed and cleared by
  // FinancialAlertsPanel — expands the sidebar's alert section and focuses
  // this rule instead of navigating away from the current page.
  financialAlertFocusRule: string | null
  fetching: boolean
  /** Why the last project read failed, and for which id. */
  loadError: { id: string; notFound: boolean } | null
  fetchingSection: boolean
  fetchingSnapshotMap: Record<FetchingSnapshotEnum, boolean>
  fetchingSubcontractors: boolean
  saveFetching: boolean
  saveResult: SaveResult
}

export type {
  ProjectState,
  ProjectFilesState,
  ProjectPageRead,
  ProjectPageReadState,
  RefreshableProjectList,
  ProjectFinancialTransactionFilters,
  ProjectInvoiceFilters,
  ProjectMilestoneFilters,
  ProjectChecklistFilters,
  ProjectCommissionsFilters,
  ProjectSubcontractorFilters,
  ProjectMilestoneFilterOptions,
  ProjectChecklistFilterOptionsKeys,
  ProjectChecklistFilterOptions,
  FetchingSnapshotEnum
}

export { FETCHING_SNAPSHOT_ENUM }
